const UNASSIGNED_LABEL = 'Not assigned';
const UNSPECIFIED_RATE_LABEL = 'Unspecified';

const asDate = value => {
    if(value instanceof Date) {
        return Number.isFinite(value.getTime()) ? value : null;
    }
    if(typeof value !== 'string' || !value) {
        return null;
    }
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
};

const normalizeCollection = (value, property) => {
    if(Array.isArray(value)) {
        return value;
    }
    return Array.isArray(value?.[property]) ? value[property] : [];
};

const parseTimeOfDay = value => {
    const match = String(value || '').trim().match(
        /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i
    );
    if(!match) {
        return null;
    }

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3] || 0);
    const meridiem = String(match[4] || '').toUpperCase();
    if(minutes > 59 || seconds > 59 || hours > (meridiem ? 12 : 23)) {
        return null;
    }
    if(meridiem) {
        hours %= 12;
        if(meridiem === 'PM') {
            hours += 12;
        }
    }
    return ((hours * 60) + minutes) * 60 + seconds;
};

const dateAtSeconds = (date, seconds, dayOffset = 0) => new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + dayOffset,
    0,
    0,
    seconds
);

const normalizeRatePeriods = value => normalizeCollection(value, 'rate_periods')
    .map(period => ({
        id: period.rowid || period.id,
        label: String(
            period.code || period.name || period.label || ''
        ).trim() || UNASSIGNED_LABEL,
        rateType: String(
            period.rate_type || period.rateType || period.type || ''
        ).trim().toLowerCase(),
        startSeconds: Number.isFinite(period.startSeconds)
            ? period.startSeconds
            : parseTimeOfDay(period.time_from || period.startTime),
        endSeconds: Number.isFinite(period.endSeconds)
            ? period.endSeconds
            : parseTimeOfDay(period.time_to || period.endTime),
    }))
    .filter(period => period.startSeconds !== null && period.endSeconds !== null);

const normalizeBillingCodes = job => {
    if(Array.isArray(job.billingCodes) && job.billingCodes.length) {
        return job.billingCodes;
    }
    const code = String(job.billingCode || '').trim();
    if(!code || code.toLowerCase() === UNASSIGNED_LABEL.toLowerCase()) {
        return [];
    }
    return [{
        id: job.billingCodeId,
        code,
        amount: job.billingAmount,
        unitMinutes: job.billingUnitMinutes || job.minutes,
    }];
};

const splitIntervalByRatePeriod = (startedAt, endedAt, ratePeriods) => {
    const start = asDate(startedAt);
    const end = asDate(endedAt);
    if(!start || !end || end <= start) {
        return [];
    }

    const periods = normalizeRatePeriods(ratePeriods);
    if(!periods.length) {
        return [{
            ratePeriodId: undefined,
            rateLabel: UNSPECIFIED_RATE_LABEL,
            rateType: '',
            startedAt: start.toISOString(),
            endedAt: end.toISOString(),
            seconds: (end - start) / 1000,
        }];
    }

    const segments = [];
    const firstDay = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() - 1
    );
    const lastDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    for(
        let day = firstDay;
        day <= lastDay;
        day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1)
    ) {
        periods.forEach(period => {
            const periodStart = dateAtSeconds(day, period.startSeconds);
            const endDayOffset = period.endSeconds <= period.startSeconds ? 1 : 0;
            const periodEnd = dateAtSeconds(day, period.endSeconds, endDayOffset);
            const segmentStart = new Date(Math.max(start, periodStart));
            const segmentEnd = new Date(Math.min(end, periodEnd));
            if(segmentEnd > segmentStart) {
                segments.push({
                    ratePeriodId: period.id,
                    rateLabel: period.label,
                    rateType: period.rateType,
                    startedAt: segmentStart.toISOString(),
                    endedAt: segmentEnd.toISOString(),
                    seconds: (segmentEnd - segmentStart) / 1000,
                });
            }
        });
    }

    const sortedSegments = segments.sort((left, right) => (
        new Date(left.startedAt) - new Date(right.startedAt)
    ));
    const completeSegments = [];
    let cursor = start;
    sortedSegments.forEach(segment => {
        const segmentStart = asDate(segment.startedAt);
        const segmentEnd = asDate(segment.endedAt);
        if(segmentStart > cursor) {
            completeSegments.push({
                ratePeriodId: undefined,
                rateLabel: UNSPECIFIED_RATE_LABEL,
                rateType: '',
                startedAt: cursor.toISOString(),
                endedAt: segmentStart.toISOString(),
                seconds: (segmentStart - cursor) / 1000,
            });
        }
        if(segmentEnd > cursor) {
            const effectiveStart = segmentStart > cursor
                ? segmentStart
                : cursor;
            completeSegments.push({
                ...segment,
                startedAt: effectiveStart.toISOString(),
                seconds: (segmentEnd - effectiveStart) / 1000,
            });
            cursor = segmentEnd;
        }
    });
    if(cursor < end) {
        completeSegments.push({
            ratePeriodId: undefined,
            rateLabel: UNSPECIFIED_RATE_LABEL,
            rateType: '',
            startedAt: cursor.toISOString(),
            endedAt: end.toISOString(),
            seconds: (end - cursor) / 1000,
        });
    }
    return completeSegments;
};

const calculateJobBilling = (job, ratePeriods, endedAt = new Date()) => {
    const effectiveEnd = job.completedAt || endedAt;
    const rateAllocations = splitIntervalByRatePeriod(
        job.billingStartedAt || job.arrivedAt,
        effectiveEnd,
        job.ratePeriods || ratePeriods
    );
    const billingCodes = normalizeBillingCodes(job);
    const allocations = rateAllocations.flatMap(rateAllocation => {
        if(!billingCodes.length) {
            return [{
                ...rateAllocation,
                billingCodeId: undefined,
                billingCode: UNASSIGNED_LABEL,
                unitMinutes: null,
                units: 0,
            }];
        }
        return billingCodes.map(billingCode => {
            const unitMinutes = Number(billingCode.unitMinutes);
            return {
                ...rateAllocation,
                billingCodeId: billingCode.id,
                billingCode: billingCode.code,
                amount: billingCode.amount,
                unitMinutes: Number.isFinite(unitMinutes) && unitMinutes > 0
                    ? unitMinutes
                    : null,
                units: Number.isFinite(unitMinutes) && unitMinutes > 0
                    ? Math.ceil(rateAllocation.seconds / (unitMinutes * 60))
                    : 0,
            };
        });
    });
    const rateLabels = [...new Set(
        rateAllocations.map(allocation => allocation.rateLabel)
    )];
    const billingCodeLabels = [...new Set(
        billingCodes.map(billingCode => billingCode.code)
    )];

    return {
        ...job,
        billingCodes,
        billingCode: billingCodeLabels.join(', ') || UNASSIGNED_LABEL,
        rateLabel: rateLabels.join(', ') || UNASSIGNED_LABEL,
        ratePeriodId: rateAllocations.length === 1
            ? rateAllocations[0].ratePeriodId
            : undefined,
        rateType: rateAllocations.length === 1
            ? rateAllocations[0].rateType
            : '',
        trackedSeconds: rateAllocations.reduce(
            (total, allocation) => total + allocation.seconds,
            0
        ),
        units: allocations.reduce((total, allocation) => total + allocation.units, 0),
        allocations,
    };
};

export {
    UNASSIGNED_LABEL,
    UNSPECIFIED_RATE_LABEL,
    calculateJobBilling,
    normalizeBillingCodes,
    normalizeRatePeriods,
    parseTimeOfDay,
    splitIntervalByRatePeriod,
};
